// Flag colours for categories of origin ("world cuisine" chips) — the small
// flag disc in the bottom-right corner of the category's icon circle.
//
// Only genuinely well-known cuisines are listed: this is a picker in the admin,
// not an atlas. Continents and regions (Asia, the Balkans) have no flag and stay
// neutral — that's `flag: null` on the category, not an entry here.
//
// Every flag is reduced to at most five stripes; emblems, crescents, stars and
// wedges are dropped, because the badge is 20 px and the label under the circle
// already names the country. The reduction makes a few flags collide (Italy /
// Mexico / India, France / Czechia / USA) — acceptable precisely because the
// colour is a tone, not the label.
//
// White is always #E8E6DF, never pure white: a pure white stripe disappears
// against the light chip background and the flag falls apart into two blocks.

export type FlagStripe = readonly [color: string, weight: number];

export type CountryFlag = {
  /** Czech country name, shown in the admin picker. */
  name: string;
  /** 'v' = stripes side by side (Italy), 'h' = bands stacked (Spain). */
  axis: 'v' | 'h';
  stripes: readonly FlagStripe[];
};

const WHITE = '#E8E6DF';

// Ordered by Czech country name — the admin picker renders them in this order.
export const COUNTRY_FLAGS = {
  czechia: {
    name: 'Česko',
    axis: 'h',
    stripes: [
      [WHITE, 1],
      ['#D7141A', 1],
    ],
  },
  china: {
    name: 'Čína',
    axis: 'v',
    stripes: [
      ['#DE2910', 3],
      ['#FFDE00', 1],
    ],
  },
  france: {
    name: 'Francie',
    axis: 'v',
    stripes: [
      ['#0055A4', 1],
      [WHITE, 1],
      ['#EF4135', 1],
    ],
  },
  india: {
    name: 'Indie',
    axis: 'h',
    stripes: [
      ['#FF9933', 1],
      [WHITE, 1],
      ['#138808', 1],
    ],
  },
  italy: {
    name: 'Itálie',
    axis: 'v',
    stripes: [
      ['#008C45', 1],
      [WHITE, 1],
      ['#CD212A', 1],
    ],
  },
  japan: {
    name: 'Japonsko',
    axis: 'h',
    stripes: [
      [WHITE, 1],
      ['#BC002D', 2],
      [WHITE, 1],
    ],
  },
  korea: {
    name: 'Korea',
    axis: 'h',
    stripes: [
      [WHITE, 1],
      ['#CD2E3A', 1],
      ['#0047A0', 1],
    ],
  },
  morocco: {
    name: 'Maroko',
    axis: 'v',
    stripes: [
      ['#C1272D', 3],
      ['#006233', 1],
    ],
  },
  mexico: {
    name: 'Mexiko',
    axis: 'v',
    stripes: [
      ['#006847', 1],
      [WHITE, 1],
      ['#CE1126', 1],
    ],
  },
  greece: {
    name: 'Řecko',
    axis: 'h',
    stripes: [
      ['#0D5EAF', 1],
      [WHITE, 1],
      ['#0D5EAF', 1],
    ],
  },
  spain: {
    name: 'Španělsko',
    axis: 'h',
    stripes: [
      ['#AA151B', 1],
      ['#F1BF00', 2],
      ['#AA151B', 1],
    ],
  },
  thailand: {
    name: 'Thajsko',
    axis: 'h',
    stripes: [
      ['#A51931', 1],
      [WHITE, 1],
      ['#2D2A4A', 2],
      [WHITE, 1],
      ['#A51931', 1],
    ],
  },
  turkey: {
    name: 'Turecko',
    axis: 'v',
    stripes: [
      ['#E30A17', 3],
      [WHITE, 1],
    ],
  },
  usa: {
    name: 'USA',
    axis: 'h',
    stripes: [
      ['#B22234', 1],
      [WHITE, 1],
      ['#3C3B6E', 1],
    ],
  },
  vietnam: {
    name: 'Vietnam',
    axis: 'h',
    stripes: [
      ['#DA251D', 3],
      ['#FFDE00', 1],
    ],
  },
} as const satisfies Record<string, CountryFlag>;

export type FlagKey = keyof typeof COUNTRY_FLAGS;

export const FLAG_OPTIONS: { key: FlagKey; flag: CountryFlag }[] = (
  Object.entries(COUNTRY_FLAGS) as [FlagKey, CountryFlag][]
).map(([key, flag]) => ({ key, flag }));

export function isFlagKey(value: string): value is FlagKey {
  return Object.prototype.hasOwnProperty.call(COUNTRY_FLAGS, value);
}

/** The category's stored flag key → its colours, or null for a neutral chip. */
export function flagByKey(key: string | null | undefined): CountryFlag | null {
  return key && isFlagKey(key) ? COUNTRY_FLAGS[key] : null;
}

/** Narrows a value read from the DB to a key the app can actually draw. */
export function toFlagKey(value: string | null | undefined): FlagKey | null {
  return value && isFlagKey(value) ? value : null;
}

// The stripes are rendered as one hard-stop gradient generated from the weights,
// so neither the colours nor the proportions live in CSS.
export function flagGradient(flag: CountryFlag): string {
  const direction = flag.axis === 'v' ? '90deg' : '180deg';
  const total = flag.stripes.reduce((sum, [, weight]) => sum + weight, 0);
  let at = 0;
  const stops = flag.stripes.map(([color, weight]) => {
    const from = (at / total) * 100;
    at += weight;
    const to = (at / total) * 100;
    return `${color} ${+from.toFixed(3)}% ${+to.toFixed(3)}%`;
  });
  return `linear-gradient(${direction}, ${stops.join(', ')})`;
}
